'use client';

import { CdnImage } from '@lmn/lmn-components';
import styled, { ThemeContext } from 'styled-components';

import { useContext } from 'react';

import type { ReactEventHandler } from 'react';
import { footprintMargin } from './footprint';
import { starfishUrl } from './starfish';
import { IconColorsContext } from './iconColors';

import type { Footprint } from './footprint';
import type { IconColors } from './iconColors';
import type { StarfishCategory, StarfishIconsByCategory } from './starfishManifest';

/** Prefer a token over a hex: only a token follows the active brand. */
export type IconColorToken = Extract<keyof IconColors['static'] | keyof IconColors['interactive'], string>;

export type IconColor = IconColorToken | `#${string}`;

type Props<C extends StarfishCategory> = {
  category: C;
  name: StarfishIconsByCategory[C];
  size: number;
  /** The layout box to keep, when it differs from `size` (see {@link footprintMargin}). */
  footprint?: Footprint;
  /** A theme token, or a `#RRGGBB` escape hatch for the few glyphs no token covers. */
  color?: IconColor;
  alt?: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  onError?: ReactEventHandler<HTMLImageElement>;
};

const Image = styled(CdnImage)<{ $margin?: string }>`
  ${({ $margin }) => ($margin ? `margin: ${$margin};` : '')}
`;

// The only shape ImageKit's colorize accepts. Anything else would put an invalid
// transformation in the URL and the image would not load at all.
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const lookup = (icons: IconColors | undefined, token: IconColorToken): string | undefined => {
  if (!icons) return undefined;

  return token in icons.static
    ? icons.static[token as keyof IconColors['static']]
    : icons.interactive[token as keyof IconColors['interactive']];
};

/**
 * Tokens come from `IconColorsContext`, which carries the raw brand values. The
 * styled theme is only a fallback for the providers that run without CSS
 * variables (unit tests, isolated previews): in the app it holds `var()`
 * references, which the hex check refuses rather than letting them reach the URL.
 *
 * ThemeContext rather than useTheme: the hook throws outside a provider.
 */
export const useIconColorHex = (color?: IconColor): string | undefined => {
  const brandIcons = useContext(IconColorsContext);
  const themeIcons = useContext(ThemeContext)?.kallisto?.color?.icon;
  if (!color) return undefined;

  const value = color.startsWith('#')
    ? color
    : (lookup(brandIcons, color as IconColorToken) ?? lookup(themeIcons, color as IconColorToken));

  return value && HEX_COLOR.test(value) ? value : undefined;
};

/**
 * Sizing is deliberately not a call-site decision. A coloured icon is served as
 * a PNG rasterised from a 24x24 source, so it has to be requested at twice the
 * rendered size or it is soft on a retina screen — and it has to be requested
 * with `fill`, because `fit` maps to `c-at_max`, which fits within the box and
 * never upscales. Both failures return HTTP 200 in the right colour, so neither
 * shows up anywhere except on a real display.
 *
 * An uncoloured icon is served as the SVG itself. ImageKit does not transform
 * SVG output, so asking it for a size would be quietly ignored — and pointless,
 * since the vector is sharp at any size. That is why the size transformation
 * only goes out alongside a colour.
 */
export const Icon = <C extends StarfishCategory>({
  category,
  name,
  size,
  footprint,
  color,
  alt = '',
  className,
  loading = 'lazy',
  onError,
}: Props<C>) => {
  const hex = useIconColorHex(color);

  return (
    <Image
      $margin={footprintMargin(size, footprint)}
      src={starfishUrl(category, name)}
      alt={alt}
      width={size}
      height={size}
      className={className}
      loading={loading}
      onError={onError}
      transformSyntax="imageKit"
      transformations={hex ? { color: hex, size: { type: 'fill', widthInPx: size * 2, heightInPx: size * 2 } } : {}}
    />
  );
};
